import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { Channel as StreamChannelType } from "stream-chat";
import { useChatContext } from "stream-chat-react";
import { Archive, ArchiveRestore, Pin, PinOff, Users } from "lucide-react";

import { UserAvatar, getInitials } from "@/features/chat/components/UserAvatar";

export const formatChannelTime = (date: Date, t: TFunction): string => {
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86400000);
  if (diffDays === 0)
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return t("chat.yesterday");
  if (diffDays < 7)
    return date.toLocaleDateString([], { weekday: "short" });
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

// Shared derivation of everything a channel row displays
const getChannelPreviewData = (
  channel: StreamChannelType,
  clientUserId: string,
  t: TFunction,
  maxPreviewLength = 45,
) => {
  const data = channel.data as any;
  const members = channel.state.members || {};
  const memberCount = Object.keys(members).length;
  const isGroup = memberCount > 2;

  const otherMember = isGroup
    ? null
    : Object.values(members).find((m) => m.user?.id !== clientUserId) ?? null;

  const displayName = data?.name
    ? (data.name as string)
    : otherMember?.user?.name || otherMember?.user?.id || "Chat";

  const displayImage = isGroup
    ? undefined
    : (otherMember?.user?.image as string | undefined);

  const isOnline = otherMember?.user?.online ?? false;

  const lastMessage =
    channel.state.messages?.[channel.state.messages.length - 1];
  const unreadCount = channel.countUnread();
  const isCallLog = !!(lastMessage as any)?.call_log;
  const rawText = lastMessage?.text?.replace(/\n/g, " ") || "";
  const preview = isCallLog
    ? `📞 ${(lastMessage as any)?.call_log_data?.call_log_type === "missed_call" ? t("chat.preview_missed_call") : t("chat.preview_call_ended")}`
    : rawText
      ? rawText.length > maxPreviewLength
        ? rawText.slice(0, maxPreviewLength) + "…"
        : rawText
      : lastMessage?.attachments?.length
        ? t("chat.preview_attachment")
        : "";

  const timeStr = lastMessage?.created_at
    ? formatChannelTime(new Date(lastMessage.created_at as any), t)
    : "";

  return { isGroup, memberCount, displayName, displayImage, isOnline, unreadCount, preview, timeStr };
};

// Standard / pinned channel row — pinned adds the amber hint + unpin action
export const ChannelPreviewItem = ({
  channel,
  active,
  clientUserId,
  pinned = false,
  onPin,
  onUnpin,
  onArchive,
}: {
  channel: StreamChannelType;
  active: boolean;
  clientUserId: string;
  pinned?: boolean;
  onPin?: () => void;
  onUnpin?: () => void;
  onArchive: () => void;
}) => {
  const { t } = useTranslation();
  const { setActiveChannel } = useChatContext();
  const {
    isGroup,
    memberCount,
    displayName,
    displayImage,
    isOnline,
    unreadCount,
    preview,
    timeStr,
  } = getChannelPreviewData(channel, clientUserId, t);

  return (
    <div
      className={`group flex items-center gap-3 mx-2 my-0.5 px-2.5 py-2.5 rounded-xl cursor-pointer transition-colors ${
        active
          ? "bg-indigo-50 ring-1 ring-indigo-100"
          : "hover:bg-slate-100/70"
      } ${pinned ? "border-l-2 border-amber-400" : ""}`}
      onClick={() => setActiveChannel(channel)}
      title={displayName}
    >
      <div className="relative flex-shrink-0">
        <UserAvatar
          name={displayName}
          id={channel.id || ""}
          image={displayImage}
          size={40}
        />
        {!isGroup && (
          <span
            className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${
              isOnline ? "bg-green-500" : "bg-slate-300"
            }`}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <span className="text-[13px] font-semibold text-slate-900 truncate">
            {displayName}
            {isGroup && (
              <span className="text-[11px] text-slate-400 font-normal ml-1">
                ({memberCount})
              </span>
            )}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            {timeStr && (
              <span className="text-[10px] text-slate-400">{timeStr}</span>
            )}
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-indigo-600 text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>
        </div>
        <p className="text-[11px] text-slate-400 truncate">{preview}</p>
      </div>
      {/* Hover actions: pin/unpin + archive */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0">
        {pinned ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUnpin?.();
            }}
            className="p-1 text-amber-400 hover:text-red-400 rounded"
            title={t("chat.unpin_conversation")}
          >
            <PinOff className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPin?.();
            }}
            className="p-1 text-slate-300 hover:text-indigo-500 rounded"
            title={t("chat.pin_conversation")}
          >
            <Pin className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onArchive();
          }}
          className="p-1 text-slate-300 hover:text-slate-500 rounded"
          title={t("chat.archive_conversation")}
        >
          <Archive className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// Channel row for the Mentions section — shows the red @mention badge
export const MentionChannelPreview = ({
  channel,
  active,
  clientUserId,
  onSelect,
}: {
  channel: StreamChannelType;
  active: boolean;
  clientUserId: string;
  onSelect: () => void;
}) => {
  const { t } = useTranslation();
  const mentionCount = channel.countUnreadMentions() ?? 0;
  const { isGroup, memberCount, displayName, displayImage, preview } =
    getChannelPreviewData(channel, clientUserId, t);

  return (
    <button
      onClick={onSelect}
      title={displayName}
      className={`w-full flex items-center gap-3 mx-2 my-0.5 px-2.5 py-2.5 rounded-xl transition-colors text-left max-w-[calc(100%-16px)] ${
        active ? "bg-indigo-50 ring-1 ring-indigo-100" : "hover:bg-amber-50/70"
      }`}
    >
      <div className="relative flex-shrink-0">
        <UserAvatar
          name={displayName}
          id={channel.id || ""}
          image={displayImage}
          size={40}
        />
        {/* @ indicator on avatar */}
        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
          <span className="text-white text-[9px] font-bold">@</span>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13px] font-semibold text-slate-900 truncate">
            {displayName}
            {isGroup && (
              <span className="text-[11px] text-slate-400 font-normal ml-1">
                ({memberCount})
              </span>
            )}
          </span>
          {mentionCount > 0 && (
            <span className="flex-shrink-0 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white min-w-[20px]">
              @{mentionCount > 99 ? "99+" : mentionCount}
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-400 truncate">{preview}</p>
      </div>
    </button>
  );
};

// Archived channel row with unarchive action
export const ArchivedChannelPreview = ({
  channel,
  onSelect,
  onUnarchive,
  clientUserId,
}: {
  channel: StreamChannelType;
  onSelect: (ch: StreamChannelType) => void;
  onUnarchive: (ch: StreamChannelType) => void;
  clientUserId: string;
}) => {
  const { t } = useTranslation();
  const { isGroup, displayName, preview } = getChannelPreviewData(
    channel,
    clientUserId,
    t,
    40,
  );

  return (
    <div className="group flex items-center gap-3 mx-2 my-0.5 px-2.5 py-2.5 rounded-xl hover:bg-slate-100/70 transition-colors">
      <button
        onClick={() => onSelect(channel)}
        title={displayName}
        className="flex items-center gap-3 flex-1 min-w-0 text-left"
      >
        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
          {isGroup ? (
            <Users className="w-5 h-5 text-slate-500" />
          ) : (
            <span className="text-sm font-bold text-slate-500">
              {getInitials(displayName, channel.id || "")}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-slate-700 truncate">
            {displayName}
          </p>
          <p className="text-xs text-slate-400 truncate">
            {preview || t("chat.no_messages_yet")}
          </p>
        </div>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onUnarchive(channel);
        }}
        className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
        title={t("chat.unarchive")}
      >
        <ArchiveRestore className="w-4 h-4" />
      </button>
    </div>
  );
};
